/**
 * Story 10.7 — dados VERBATIM dos embeds de terceiro do Menu do Trader (CAP-14).
 *
 * Estes valores são CONTEÚDO DE TERCEIRO copiado byte-a-byte da produção viva
 * (virtualeurope.net, verificado em 2026-07-22) — secção "Embeds verbatim" da spec.
 * NÃO editar `src`/`config`: nenhum número, símbolo ou claim de mercado é escrito por nós
 * (regra dura de zero fabricação do épico). Só a largura/altura do WRAPPER de layout é nossa,
 * e vive nos componentes, não aqui.
 *
 * O que a produção NÃO serve (revisões PT antigas, superseded na migração PT→BR) e por isso
 * NÃO está aqui: bloco "Notícias Financial Juice", iframes `pt.widgets.investing.com`
 * (top-cryptocurrencies / live-currency-cross-rates). Ver Design Notes da spec.
 *
 * O banner de afiliado da Axi ficou de fora da 10.7 (decisão de dinheiro do Diego). Em 2026-07-22 o
 * Diego decidiu INCLUIR — "produção é a lei" — e a Story 10.11 trá-lo para cá (`AXI_BANNER`, abaixo).
 */

/** Config de um widget TradingView, tal como a produção o serializa. */
export type TradingViewConfig = Record<string, unknown>

/** Todos os loaders TradingView vêm deste host (`<nome>.js`). */
const TRADINGVIEW_SCRIPT_BASE =
  "https://s3.tradingview.com/external-embedding/"

function tradingViewScript(widgetName: string): string {
  return `${TRADINGVIEW_SCRIPT_BASE}${widgetName}.js`
}

/** Copyright obrigatório de todos os widgets TradingView (link verbatim). */
export const TRADINGVIEW_COPYRIGHT = {
  label: "Monitore todos os mercados no TradingView",
  href: "https://br.tradingview.com/",
} as const

// ── Calendário (Investing.com) ────────────────────────────────────────────────
// iframe sslecal2 — `src` VERBATIM (query string intocada).
export const CALENDAR_IFRAME_SRC =
  "https://sslecal2.investing.com?columns=exc_flags,exc_currency,exc_importance,exc_actual,exc_forecast,exc_previous&importance=2,3&features=datepicker,timezone,timeselector,filters&countries=17,25,32,6,37,26,5,22,39,14,10,35,7,43,38,4,36,12,72&calType=day&timeZone=12&lang=12"

/** Crédito obrigatório do calendário (link verbatim). */
export const CALENDAR_CREDIT = {
  label: "Calendário Econômico fornecido por Investing.com Brasil",
  href: "https://br.investing.com/",
} as const

// ── Banner de afiliado da Axi (Story 10.11) ───────────────────────────────────
// `href` e `src` são VERBATIM da produção, byte-a-byte: o `promocode` é o identificador de afiliado
// e a barra dupla em `//Content` é assim que a produção a serve — NÃO normalizar nenhum dos dois.
// Só o `alt` é nosso (a produção deixa-o vazio): descreve o anunciante e o destino do link, sem
// qualquer claim acrescentado.
export const AXI_BANNER = {
  href: "https://www.axi.com/int/live-account?promocode=4704543",
  src: "https://axiapi2.fynxt.com//Content/IBBanner/0e9a07b1-ca0b-4303-8ee3-ca99abb8cc4b.jpg",
  alt: "Banner do parceiro Axi: abrir conta no site da Axi (abre em nova aba)",
} as const

// ── Visão Geral do Mercado (TradingView market-overview) ───────────────────────
export const MARKET_OVERVIEW_SCRIPT = tradingViewScript(
  "embed-widget-market-overview"
)
export const MARKET_OVERVIEW_CONFIG: TradingViewConfig = {"colorTheme":"dark","dateRange":"1D","showChart":true,"locale":"br","largeChartUrl":"","isTransparent":false,"showSymbolLogo":true,"showFloatingTooltip":true,"width":"100%","height":"850","plotLineColorGrowing":"rgba(41, 98, 255, 1)","plotLineColorFalling":"rgba(41, 98, 255, 1)","gridLineColor":"rgba(242, 242, 242, 0)","scaleFontColor":"rgba(219, 219, 219, 1)","belowLineFillColorGrowing":"rgba(41, 98, 255, 0.12)","belowLineFillColorFalling":"rgba(41, 98, 255, 0.12)","belowLineFillColorGrowingBottom":"rgba(41, 98, 255, 0)","belowLineFillColorFallingBottom":"rgba(41, 98, 255, 0)","symbolActiveColor":"rgba(41, 98, 255, 0.12)","tabs":[{"title":"Indices","symbols":[{"s":"FOREXCOM:SPXUSD","d":"S&P 500 Index"},{"s":"FOREXCOM:NSXUSD","d":"US 100 Cash CFD"},{"s":"FOREXCOM:DJI","d":"Dow Jones Industrial Average Index"},{"s":"INDEX:NKY","d":"Japan 225"},{"s":"INDEX:DEU40","d":"DAX Index"},{"s":"FOREXCOM:UKXGBP","d":"FTSE 100 Index"}],"originalTitle":"Indices"},{"title":"Futures","symbols":[{"s":"CME_MINI:ES1!","d":"S&P 500"},{"s":"CME:6E1!","d":"Euro"},{"s":"COMEX:GC1!","d":"Gold"},{"s":"NYMEX:CL1!","d":"WTI Crude Oil"},{"s":"NYMEX:NG1!","d":"Gas"},{"s":"CBOT:ZC1!","d":"Corn"}],"originalTitle":"Futures"},{"title":"Bonds","symbols":[{"s":"CBOT:ZB1!","d":"T-Bond"},{"s":"CBOT:UB1!","d":"Ultra T-Bond"},{"s":"EUREX:FGBL1!","d":"Euro Bund"},{"s":"EUREX:FBTP1!","d":"Euro BTP"},{"s":"EUREX:FGBM1!","d":"Euro BOBL"}],"originalTitle":"Bonds"},{"title":"Forex","symbols":[{"s":"FX:EURUSD","d":"EUR to USD"},{"s":"FX:GBPUSD","d":"GBP to USD"},{"s":"FX:USDJPY","d":"USD to JPY"},{"s":"FX:USDCHF","d":"USD to CHF"},{"s":"FX:AUDUSD","d":"AUD to USD"},{"s":"FX:USDCAD","d":"USD to CAD"}],"originalTitle":"Forex"}]}

// ── Cotações Cripto (TradingView) ──────────────────────────────────────────────
// A: screener (crypto_mkt)
export const CRYPTO_SCREENER_SCRIPT = tradingViewScript("embed-widget-screener")
export const CRYPTO_SCREENER_CONFIG: TradingViewConfig = {"width":"100%","height":"800","defaultColumn":"overview","screener_type":"crypto_mkt","displayCurrency":"USD","colorTheme":"dark","locale":"br","isTransparent":true}

// B: mapa de calor das criptos
export const CRYPTO_HEATMAP_SCRIPT = tradingViewScript(
  "embed-widget-crypto-coins-heatmap"
)
export const CRYPTO_HEATMAP_CONFIG: TradingViewConfig = {"dataSource":"Crypto","blockSize":"market_cap_calc","blockColor":"change","locale":"br","symbolUrl":"","colorTheme":"dark","hasTopBar":false,"isDataSetEnabled":false,"isZoomEnabled":true,"hasSymbolTooltip":true,"isMonoSize":false,"width":1004,"height":900}

// ── Gráficos (TradingView advanced-chart) ──────────────────────────────────────
export const ADVANCED_CHART_SCRIPT = tradingViewScript(
  "embed-widget-advanced-chart"
)
export const ADVANCED_CHART_CONFIG: TradingViewConfig = {"width":"1400","height":"900","symbol":"FX:EURUSD","interval":"D","timezone":"America/Sao_Paulo","theme":"dark","style":"1","locale":"br","withdateranges":true,"hide_side_toolbar":false,"allow_symbol_change":true,"watchlist":["OANDA:XAUUSD","OANDA:USDJPY","OANDA:GBPUSD","BITSTAMP:BTCUSD"],"details":true,"calendar":false,"studies":["STD;SMA","STD;Divergence%1Indicator"],"support_host":"https://www.tradingview.com"}
